import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { SubscriptionService } from '@/services/subscriptionService';
import { SubscriptionStatus, FeatureKey, FeatureCheckResult } from '@/types/subscription';
import { supabase } from '@/integrations/supabase/client';
import { useRestaurant } from '@/hooks/useRestaurant';

interface SubscriptionContextType {
  subscription: SubscriptionStatus | null;
  isLoading: boolean;
  error: Error | null;
  checkAccess: (feature: FeatureKey, currentUsage?: number) => Promise<FeatureCheckResult>;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, staffSession, isStaff } = useAuth();
  const { restaurant: ownerRestaurant } = useRestaurant(); // Only works for owners
  
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Determine the active restaurant ID
  const restaurantId = isStaff 
    ? staffSession?.restaurantId 
    : ownerRestaurant?.id;

  const fetchSubscription = useCallback(async () => {
    if (!restaurantId) {
      setSubscription(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const sub = await SubscriptionService.getSubscription(restaurantId);
      setSubscription(sub);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch subscription', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [restaurantId]);

  // Initial Fetch
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Realtime Subscription
  useEffect(() => {
    if (!restaurantId) return;

    const channel = supabase
      .channel('subscription-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tenant_subscriptions',
          filter: `restaurant_id=eq.${restaurantId}`
        },
        (payload) => {
          console.log('Subscription updated realtime:', payload);
          fetchSubscription(); // Refetch to get fresh data (especially cached_features)
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [restaurantId, fetchSubscription]);

  const checkAccess = useCallback(async (feature: FeatureKey, currentUsage?: number): Promise<FeatureCheckResult> => {
    if (!restaurantId) return { allowed: false, reason: 'past_due' };
    return SubscriptionService.checkFeatureAccess(restaurantId, feature, currentUsage);
  }, [restaurantId]);

  return (
    <SubscriptionContext.Provider value={{ 
      subscription, 
      isLoading, 
      error, 
      checkAccess,
      refreshSubscription: fetchSubscription 
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscriptionContext = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
};
